import { DATE_FORMAT_DAY_MONTH_YEAR } from '@constants/datetime';
import { EventReportBookingOptions } from '@contexts/event-report/event-report-context.type';
import { Event } from '@domain/event';
import { EventReportDataType, EventSalesStatsResponse } from '@domain/event-report';
import { VIEW_LIVE, VIEW_PAST, VIEW_UPCOMING } from '@hooks/use-event-navigation/use-event-navigation';
import { formatDateTime } from '@utils/date-time-format';
import { fromBackend } from '@utils/map-date/from-backend';
import { formatValue } from '@utils/price-formatter/price-formatter';
import { TFunction } from 'i18next';
import moment from 'moment';

import { currencyValueFormatter } from '../analytics-utils';

import {
  BADGE_LIST_ADMISSIONS,
  EVENT_REPORT_BOTTLE_SERVICE_TYPE,
  EVENT_REPORT_CHECKIN_TYPE,
  EVENT_REPORT_COLORS,
  EVENT_REPORT_GUEST_LIST_TYPE,
  EVENT_REPORT_INVITATION_TYPE,
  EVENT_REPORT_PASS_TYPE,
  EVENT_REPORT_TAB_TYPE_REVENUE,
  EVENT_REPORT_TICKET_TYPE,
} from './config';
import { TableChannelReport, TableOfferReport } from './types';

export const getEventState = (event?: Event) => {
  if (!event) return VIEW_UPCOMING;

  // Get current time in the event's timezone
  const nowInEventTimezone = moment().tz(event.club.city.timezone).unix();

  if (nowInEventTimezone < event.startingTime) return VIEW_UPCOMING;
  if (nowInEventTimezone >= event.startingTime && event.endingTime >= nowInEventTimezone) return VIEW_LIVE;

  return VIEW_PAST;
};

export const isParentColumn = (id: string) => {
  return [
    EVENT_REPORT_TICKET_TYPE,
    EVENT_REPORT_BOTTLE_SERVICE_TYPE,
    EVENT_REPORT_GUEST_LIST_TYPE,
    EVENT_REPORT_PASS_TYPE,
    EVENT_REPORT_INVITATION_TYPE,
    EVENT_REPORT_CHECKIN_TYPE,
  ].includes(id);
};

export const isChildColumn = (id: string) => {
  return (
    id.includes(EVENT_REPORT_TICKET_TYPE) ||
    id.includes(EVENT_REPORT_BOTTLE_SERVICE_TYPE) ||
    id.includes(EVENT_REPORT_GUEST_LIST_TYPE) ||
    id.includes(EVENT_REPORT_PASS_TYPE) ||
    id.includes(EVENT_REPORT_INVITATION_TYPE) ||
    id.includes(EVENT_REPORT_CHECKIN_TYPE)
  );
};

export const changeColumnVisibility = ({
  table,
  index,
}: {
  table: TableOfferReport | TableChannelReport;
  index: number;
}) => {
  const parentColumn =
    table.columns.filter((column) => column.isActive)[index].dependsOn ??
    table.columns.filter((column) => column.isActive)[index].id;

  return {
    ...table,
    columns: table.columns.map((column) => {
      if (column.dependsOn === parentColumn) {
        return {
          ...column,
          isActive: !column.isActive,
        };
      }
      if (column.id === parentColumn) {
        return {
          ...column,
          isClicked: !column.isClicked,
        };
      }
      return column;
    }),
  };
};

export const formatValueEventReportPage = ({
  value,
  type,
  locale,
  currency,
}: {
  value: number;
  type: string;
  locale: string;
  currency: string;
}) => {
  if (type === EVENT_REPORT_TAB_TYPE_REVENUE) {
    return currencyValueFormatter(value, locale, currency);
  }

  return formatValue({
    number: value,
    shortened: false,
  });
};

export const headerDateFormat = (date: number, lang: string, timezone: string) => {
  return formatDateTime({
    date: fromBackend(date, timezone).toDate(),
    locale: lang,
    format: DATE_FORMAT_DAY_MONTH_YEAR,
    timezone,
  });
};

export const createDefaultAdmissionBadges = (t: TFunction<'translation', undefined>) => {
  return BADGE_LIST_ADMISSIONS.map((badge) => ({
    id: badge.id as EventReportDataType,
    color: badge.color,
    label: t(badge.title),
    isActive: true,
  }));
};

export const createDefaultChannelsBadges = (t: TFunction, selectedChannels: { id: string; name: string }[] = []) => {
  const colors = EVENT_REPORT_COLORS;

  if (selectedChannels.length === 0) {
    return [{ id: 'total', color: colors[0], label: t('eventReport:filterByChannelLabel'), isActive: true }];
  }

  return Array.from(new Set(selectedChannels.map((channel) => channel.name))).map((name, index) => ({
    id: name,
    color: colors[index % colors.length],
    label: name,
    isActive: true,
  }));
};

export const createFilteredAdmissionBadges = (
  t: TFunction<'translation', undefined>,
  eventSales: EventSalesStatsResponse,
  bookingFilterValue: EventReportBookingOptions,
  badges: typeof BADGE_LIST_ADMISSIONS = BADGE_LIST_ADMISSIONS
) => {
  return badges
    .filter(
      (badge) =>
        eventSales[badge.id as EventReportDataType].revenue[bookingFilterValue] > 0 ||
        eventSales[badge.id as EventReportDataType].soldItems[bookingFilterValue] > 0 ||
        eventSales[badge.id as EventReportDataType].attendees[bookingFilterValue] > 0
    )
    .map((badge) => ({
      id: badge.id as EventReportDataType,
      color: badge.color,
      label: t(badge.title),
      isActive: true,
    }));
};
